import { DirectDebitRunPayment } from "../../../model/payments/DirectDebitRunPayment";
import dayjs from "dayjs";
import { Card, Table, Tag } from "antd";
import type { SortOrder } from 'antd/es/table/interface';
import { useFormatCurrency } from '../../../hooks/useFormatters';

interface DirectDebitPaymentsViewProps {
    payments: DirectDebitRunPayment[];
    backgroundColor?: string;
    borderColor?: string;
}

const DirectDebitPaymentsView: React.FC<DirectDebitPaymentsViewProps> = ({ 
    payments,
    backgroundColor = '#ffffff',
    borderColor = '#e5e7eb'
}) => {
    const formatCurrency = useFormatCurrency();
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Successful':
                return 'success';
            case 'Failed':
                return 'error';
            case 'Pending':
                return 'warning';
            case 'Cancelled':
                return 'default';
            case 'Rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const columns = [
        {
            title: 'Payment Date',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            render: (date: Date) => dayjs(date).format('ddd DD MMM YYYY'),
            sorter: (a: DirectDebitRunPayment, b: DirectDebitRunPayment) => 
                dayjs(a.paymentDate).unix() - dayjs(b.paymentDate).unix(),
            defaultSortOrder: 'descend' as SortOrder
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number) => formatCurrency(amount),
            sorter: (a: DirectDebitRunPayment, b: DirectDebitRunPayment) => a.amount - b.amount
        },
        {
            title: 'Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>
                    {status}
                </Tag>
            ),
            sorter: (a: DirectDebitRunPayment, b: DirectDebitRunPayment) => 
                a.paymentStatus.localeCompare(b.paymentStatus)
        }
    ];

    return (
        <Card 
            title="Direct Debit Payments"
            className="mb-4"
            style={{ 
                backgroundColor,
                borderColor
            }}
        >
            <Table
                columns={columns}
                dataSource={payments}
                pagination={{ pageSize: 5 }}
                rowKey={(record) => `${record.paymentDate}-${record.reference}`}
                rowClassName={(record) => record.paymentStatus !== 'Successful' ? 'highlight-row' : ''}
                scroll={{ x: 'max-content' }}
            />
        </Card>
    );
};

export default DirectDebitPaymentsView; 